import { motivoPerdaSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Campo, Checkbox, Input } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/motivos-perda')({ component: MotivosPerda })

type Item = NonNullable<ReturnType<typeof useMotivos>['data']>[number]
function useMotivos() {
  const trpc = useTRPC()
  return useQuery(trpc.cadastros.motivosPerda.listar.queryOptions())
}

function MotivosPerda() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useMotivos()
  const g = useGavetaCadastro<Item>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.motivosPerda.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.motivosPerda.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.motivosPerda.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: motivoPerdaSchema,
    numericos: ['ordem'],
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Motivo ${d.nome} salvo.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <>
      <BarraTitulo
        titulo="Motivos de perda"
        descricao="Quando o operador conclui com menos peças e diz que foi perda, ele escolhe um destes motivos. Se as peças só ficaram para depois, viram uma ficha de saldo e não entram aqui."
        onNovo={g.abrirNovo}
        rotuloNovo="Novo motivo"
      />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Ordem</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((m) => (
            <Linha key={m.id}>
              <Td className="font-medium">
                {m.nome} {!m.ativo && <Ativo ativo={false} />}
              </Td>
              <Td numerico>{m.ordem}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={m.nome} onClick={() => g.abrirEditar(m)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={3} />}
          {lista.data?.length === 0 && <Vazio colunas={3}>Nenhum motivo cadastrado.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar motivo de perda' : 'Novo motivo de perda'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-motivo-perda"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario
          id="form-motivo-perda"
          onSubmit={submit}
          erros={g.erros}
          onLimparErro={g.limparErro}
        >
          <Campo label="Nome (no botão do tablet)" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo label="Ordem no tablet" erro={g.erros.ordem}>
            {(p) => (
              <Input {...p} name="ordem" type="number" min={0} defaultValue={g.item?.ordem ?? 0} />
            )}
          </Campo>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </>
  )
}
